import { apiClient } from './client';

// Mirrors packages/shared/src/types/search-visibility.ts (the web app keeps its
// own copy, as gtm.ts and web-analytics.ts do).

export interface SearchVisibilityCampaign {
  campaignId: string;
  market: string;
  countryCode: string | null;
  language: string | null;
  device: string | null;
  keywordsCount: number | null;
}

export interface SearchVisibilityKpis {
  visibility: number | null;
  /** Percentage-point change across the period, not a percent change. */
  visibilityChange: number | null;
  shareOfVoice: number | null;
  shareOfVoiceChange: number | null;
  avgPosition: number | null;
  avgPositionChange: number | null;
  estimatedTraffic: number | null;
  estimatedTrafficChange: number | null;
  keywordsTracked: number;
  top3: number;
  top10: number;
  entered: number;
  left: number;
}

export interface SearchVisibilityTrendPoint {
  date: string;
  domains: Record<string, number | null>;
}

export interface SearchVisibilityDomainRow {
  domain: string;
  isSelf: boolean;
  visibility: number | null;
  visibilityChange: number | null;
  estimatedTraffic: number | null;
  estimatedTrafficChange: number | null;
  avgPosition: number | null;
  shareOfVoice: number | null;
}

/**
 * One week of our own performance. Each row is a snapshot on the week's last
 * harvest day, not an average — visibility is a state, so an average would
 * report a figure that never occurred.
 */
export interface SearchVisibilityWeekRow {
  weekStart: string;
  weekEnd: string;
  /** Harvest days averaged into the week: fewer than 7 for the newest week, or when Semrush skipped days. */
  harvestDays?: number;
  visibility: number | null;
  visibilityChange: number | null;
  shareOfVoice: number | null;
  estimatedTraffic: number | null;
  estimatedTrafficChange: number | null;
  top3: number | null;
  top10: number | null;
  rank: number | null;
  competitorCount: number;
}

export type SearchVisibilityGranularity = 'daily' | 'weekly';

export interface SearchVisibilityRankPoint {
  date: string;
  top3: number;
  top4to10: number;
  top11to20: number;
  top21to100: number;
  notRanking: number;
}

export interface SearchVisibilityKeywordRow {
  keyword: string;
  position: number | null;
  change: number | null;
  searchVolume: number | null;
  tags: string[];
}

export interface SearchVisibilityLandingPageRow {
  url: string;
  estimatedTraffic: number | null;
  estimatedTrafficChange: number | null;
  keywordsCount: number | null;
  avgPosition: number | null;
}

export interface SearchVisibilityMarketSummary {
  campaignId: string;
  market: string;
  countryCode: string | null;
  visibility: number | null;
  visibilityChange: number | null;
  estimatedTraffic: number | null;
  rank: number | null;
  competitorCount: number;
}

export interface SearchVisibilityData {
  period: { startDate: string; endDate: string };
  granularity: SearchVisibilityGranularity;
  campaignId: string | null;
  campaigns: SearchVisibilityCampaign[];
  markets: SearchVisibilityMarketSummary[];
  kpis: SearchVisibilityKpis | null;
  trend: SearchVisibilityTrendPoint[] | null;
  leaderboard: SearchVisibilityDomainRow[] | null;
  rankDistribution: SearchVisibilityRankPoint[] | null;
  weeks: SearchVisibilityWeekRow[] | null;
  keywords: SearchVisibilityKeywordRow[] | null;
  landingPages: SearchVisibilityLandingPageRow[] | null;
  lastCapturedDate: string | null;
  firstCapturedDate: string | null;
  /** Days averaged at each end of the period for KPI, market and leaderboard figures and their changes. */
  averageDays?: number | null;
  /** Sync health for the freshness chip (see the shared type). Absent on older payloads. */
  sync?: SearchVisibilitySyncStatus | null;
  unavailable: string[];
}

export async function getSearchVisibility(
  campaignId?: string,
  startDate?: string,
  endDate?: string,
  granularity?: SearchVisibilityGranularity,
): Promise<SearchVisibilityData> {
  return apiClient.get<SearchVisibilityData>('/search-visibility', {
    params: { campaignId, startDate, endDate, granularity },
  });
}

export interface SearchVisibilitySyncStatus {
  /** Days between real Position Tracking pulls (throttled to save Semrush API units). */
  refreshEveryDays: number;
  lastPulledAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunError: string | null;
}
